import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_STAKEHOLDER_GROUP,
  DELETE_STAKEHOLDER_GROUP,
  FIND_ALL_STAKEHOLDER_GROUPS_BY_COMMUNICATION_PLAN,
  FIND_COMMUNICATION_PLAN_BY_PLAN,
  UPDATE_STAKEHOLDER_GROUP,
} from '../../../queries';
import TitleBar from '../../../../../components/TitleBar';
import { Footer } from '../../../../../components/common/Footer';
import { CommunicationSection } from '..';
import useBCPMetaData from '../../../../../hooks/useBCPMetaData';
import { Loading } from '../../../../../components';
import BorderedSection from '../../../../../components/generic/BorderedSection';

import { ColumnsType } from 'antd/es/table';
import { tableSortingMethods } from '../../../../../utils';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import QueryErrorBoundary from '../../../../../components/error-handling';
import { fields } from './formFields';
import { baseColumns } from './exportColumns';
import TitleUpdater from '../../../../../components/common/TitleUpdater';

export type CrisisCommunication = {
  communicationPlanInPlace: boolean;
  email: string;
  lastUpdate: string;
  planOwnerFirstName: string;
  planOwnerLastName: string;
  uuid: string;
};

export type Stakeholder = {
  contactFirstName: string;
  contactLastName: string;
  email: string;
  name: string;
  phone: string;
  uuid: string;
};

export default function CrisisCommunication() {
  const params = useParams();
  const { type, locationId } = params;

  const isLocationType = type === 'location';

  const { planUUID, locationName, loading, error } = useBCPMetaData(
    locationId!,
    isLocationType,
  );

  const backPath = `/bcp-landing/${type}/${locationId}`;

  const columns: ColumnsType<any> = [
    {
      title: 'Stakeholder Group',
      key: 'name',
      width: 150,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (_, { name }) => (
        <span className="custom-table--column custom-table--column-primary">
          {name}
        </span>
      ),
    },
    {
      title: 'Assigned Person',
      key: 'assignedPerson',
      align: 'left',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.string(a.contactFirstName, b.contactFirstName),
      render: (_, { contactFirstName, contactLastName }) =>
        contactFirstName || contactLastName ? (
          <div className="custom-table--column">
            {contactFirstName} {contactLastName}
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'Contact',
      key: 'contact',
      align: 'left',
      width: 150,
      sorter: (a, b) => tableSortingMethods.string(a.email, b.email),
      render: (_, { email, phone }) =>
        email || phone ? (
          <div className="flex flex-col custom-table--column text-sm">
            <span>{email}</span>
            <span>{phone}</span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const { data } = useQuery(FIND_COMMUNICATION_PLAN_BY_PLAN, {
    variables: { uuid: planUUID },
  });

  const {
    data: stakeholdersData,
    loading: stakeholdersLoading,
    error: stakeholdersError,
  } = useQuery(FIND_ALL_STAKEHOLDER_GROUPS_BY_COMMUNICATION_PLAN, {
    variables: { uuid: data?.findCommunicationPlanByPlan.uuid },
    skip: !data?.findCommunicationPlanByPlan,
  });

  const [createGQL] = useMutation(CREATE_STAKEHOLDER_GROUP, {
    refetchQueries: ['FindAllStakeHolderGroupsByCommunicationPlan'],
  });

  const [updateGQL] = useMutation(UPDATE_STAKEHOLDER_GROUP, {
    refetchQueries: ['FindAllStakeHolderGroupsByCommunicationPlan'],
  });

  const [deleteGQL] = useMutation(DELETE_STAKEHOLDER_GROUP, {
    refetchQueries: ['FindAllStakeHolderGroupsByCommunicationPlan'],
  });

  const addVariables = ({ values, type }: ModifyVariablesArgs) => {
    if (type === 'create') {
      return {
        ...values,
        communicationPlan: { uuid: data?.findCommunicationPlanByPlan.uuid },
      };
    }
    return {
      ...values,
    };
  };

  return (
    <>
      <TitleUpdater title={`Crisis Communication`} />
      <TitleBar
        title="BCP Hub"
        subtitle="Crisis Communication"
        brief="During a prolonged disruption communication with interested parties is critical. List who
                      is responsible for communicating with the respective stakeholders below."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Stakeholder Groups"
          columns={columns}
          data={stakeholdersData?.findAll}
          loading={stakeholdersLoading}
          error={stakeholdersError}
          editable
          formFields={fields}
          createGQL={createGQL}
          updateGQL={updateGQL}
          deleteGQL={deleteGQL}
          modifyVariables={addVariables}
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <div className="min-w-[600px] mt-6 flex flex-col flex-nowrap justify-center items-center mb-12">
        <BorderedSection
          title={`${
            isLocationType ? locationName : 'Corporate'
          } Crisis Communication Plan`}
        >
          {!loading ? <CommunicationSection planId={planUUID} /> : <Loading />}
        </BorderedSection>
      </div>
      <Footer backPath={backPath} />
    </>
  );
}
